/**
 * MasterDataHolidayFormDialog — Create/Edit `market_holiday`.
 * Compact Sales Contract style.
 */
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
} from '@/store/api/masterDataCrudApi'
import type { MarketHoliday } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleSx,
  roSx,
} from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MarketHoliday | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = { date: string; market: string; holidays: string }

const EMPTY: FormState = { date: '', market: '', holidays: '' }

// Access ComboBox `market` — RowSourceType="Value List", RowSource="LRC";"KCE".
const MARKET_OPTIONS = ['LRC', 'KCE']

export default function MasterDataHolidayFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createHoliday, { isLoading: creating }] = useCreateHolidayMutation()
  const [updateHoliday, { isLoading: updating }] = useUpdateHolidayMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [err, setErr] = useState<string | null>(null)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setErr(null)
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        date: initialValue.date ?? '',
        market: initialValue.market ?? '',
        holidays: initialValue.holidays ?? '',
      })
    } else {
      // New → auto-load Date = Now (hôm nay); user chỉ cần đổi nếu khác.
      setForm({ ...EMPTY, date: dayjs().format('YYYY-MM-DD') })
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const dateEmpty = !form.date
  const marketEmpty = !form.market.trim()
  const holidaysEmpty = !form.holidays.trim()
  const canSave = !dateEmpty && !marketEmpty && !holidaysEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (dateEmpty || marketEmpty || holidaysEmpty) {
      setShowErrors(true)
      return
    }
    setErr(null)
    try {
      if (isEdit && initialValue) {
        await updateHoliday({
          date: initialValue.date,
          market: initialValue.market,
          body: { holidays: form.holidays.trim() },
        }).unwrap()
        snack.success('Holiday updated')
      } else {
        await createHoliday({
          date: form.date,
          market: form.market.trim(),
          holidays: form.holidays.trim(),
        } as MarketHoliday).unwrap()
        snack.success('Holiday created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
          {isEdit ? 'Edit Holiday' : 'New Holiday'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.date} / {initialValue.market}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {err ? (
          <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
            {err}
          </Typography>
        ) : null}
        {/* Access single-column layout (all inputs Left=2866), order by Top:
            Date (283) → Market (729) → Holiday (1204). */}
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <DatePicker
              label="Date"
              format="DD-MMM-YYYY"
              disabled={isEdit}
              value={form.date ? dayjs(form.date) : null}
              onChange={(d) => set('date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true,
                  error: showErrors && dateEmpty,
                  InputLabelProps: { shrink: true },
                  inputProps: { tabIndex: 1 },
                  sx: isEdit ? roSx : undefined,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              fullWidth
              disabled={isEdit}
              options={MARKET_OPTIONS}
              value={form.market || null}
              onChange={(_e, v) => set('market', v ?? '')}
              getOptionLabel={(o) => o ?? ''}
              isOptionEqualToValue={(a, b) => a === b}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Market"
                  required
                  error={showErrors && marketEmpty}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ ...params.inputProps, tabIndex: 2 }}
                  sx={isEdit ? roSx : undefined}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Holiday"
              size="small"
              fullWidth
              required
              error={showErrors && holidaysEmpty}
              value={form.holidays}
              onChange={(e) => set('holidays', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
